import Container from '@brands/Components/Container/Container';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import DentalHistory from '@brands/Dashboard/MedicalHistory/DentalHistory/DentalHistory';
import MedicalHistory from '@brands/Dashboard/MedicalHistory/MedicalHistory/MedicalHistory';
import { useWindowSize } from '@brands/hooks';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Profile from './Sections/Profile/Profile';
import ProfileCommonHeader from './Sections/ProfileCommonHeader/ProfileCommonHeader';
import styles from './styles.module.scss';

const MyProfile = (): JSX.Element => {
  const screenSize = useWindowSize();
  const { state } = useLocation();
  const [currentTab, setCurrentTab] = useState('profile');
  const formRef = useRef<HTMLFormElement>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Owned here (not in the Profile tab) so the "editing a dependent" context survives switching to
  // the Medical/Dental History tabs and back — those tabs unmount Profile, which used to reset it.
  // Only saving a change clears it (EditDependent calls setIsEditDependent(false)), returning to the guardian.
  const [dependentId, setDependentId] = useState<number | undefined>(state?.selectedDependentId ?? undefined);
  const [isEditDependent, setIsEditDependent] = useState<boolean>(state?.openDependentProfile ?? false);

  const { showModal, confirmNavigation, saveChanges, hideModal, handleBlockedNavigation } = useUnsavedChangesWarning(
    isDirty,
    formRef,
    undefined,
    () => setIsDirty(false),
  );

  const myProfileTabs = [
    { label: 'Profile', value: 'profile' },
    { label: 'Medical history', value: 'medical_history' },
    { label: 'Dental history', value: 'dental_history' },
  ];

  const handleTabChange = (nextTab: string): void => {
    if (nextTab === currentTab) return;
    handleBlockedNavigation(() => {
      setCurrentTab(nextTab);
      window.history.replaceState(null, '', `#${nextTab}`);
    });
  };

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (myProfileTabs.find((t) => t.value === hash)) {
      setCurrentTab(hash);
    }
  }, []);

  return (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      containerStyle={{
        justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
        overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
      }}
    >
      <div className={styles.myProfileContainer}>
        <ProfileCommonHeader
          currentTab={currentTab}
          handleTabChange={handleTabChange}
          tabs={myProfileTabs}
          // Same correction as My Care: an unselected tab sat on MUI's default
          // grey rather than the product navy. Size and weight now follow the
          // prototypes on the shared header; only the colour is set here.
          sx={{ tab: { color: 'var(--color-text-primary)' } }}
        />

        {currentTab === 'profile' && (
          <Profile
            isProfileCard
            formRef={formRef}
            setIsDirty={setIsDirty}
            isEditDependent={isEditDependent}
            setIsEditDependent={setIsEditDependent}
            dependentId={dependentId}
            setDependentId={setDependentId}
          />
        )}
        {currentTab === 'medical_history' && <MedicalHistory isProfileCard formRef={formRef} setIsDirty={setIsDirty} />}
        {currentTab === 'dental_history' && <DentalHistory isProfileCard formRef={formRef} setIsDirty={setIsDirty} />}

        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={saveChanges}
            loading={false}
            hideModal={hideModal}
          />
        )}
      </div>
    </Container>
  );
};

export default MyProfile;
